import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-all duration-300 ease-apple focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.97] active:opacity-90 touch-manipulation",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/90",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        cta: "bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold shadow-soft hover:shadow-medium",
        "cta-outline": "border-2 border-secondary bg-transparent text-secondary hover:bg-secondary hover:text-secondary-foreground font-semibold",
        whatsapp: "bg-[#25D366] text-white hover:bg-[#128C7E] font-medium",
        // Apple-style minimal button
        apple: "bg-primary text-primary-foreground hover:bg-primary/90 font-medium",
        "apple-secondary": "bg-muted text-foreground hover:bg-muted/80 font-medium",
        // Liquid Glass variants
        glass: "glass-button font-semibold",
        "glass-cta": "glass-button font-semibold bg-gradient-to-b from-secondary/95 to-secondary text-secondary-foreground hover:from-secondary hover:to-secondary/90 border-secondary/40",

      },
      size: {
        default: "h-11 px-5 py-2.5 min-h-[44px]",
        sm: "h-10 px-4 text-sm min-h-[40px]",
        lg: "h-12 px-8 text-base min-h-[48px]",
        xl: "h-14 px-10 text-lg min-h-[56px]",
        icon: "h-11 w-11 min-h-[44px] min-w-[44px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };